import React, { useState } from 'react';
import { Bell, X, AlertCircle, TrendingUp, Clock, Check, RefreshCw, Calendar, AlertTriangle } from 'lucide-react';
import { useNotificaciones } from '../contexts/NotificacionesContext';

function NotificacionesCampana({ onContenedorClick }) {
  const {
    notificaciones,
    resumen,
    loading,
    marcarComoLeida,
    marcarTodasComoLeidas,
    descartarNotificacion,
    forzarRecalculo,
    ultimaActualizacion
  } = useNotificaciones();

  const [showModal, setShowModal] = useState(false);
  const [recalculando, setRecalculando] = useState(false);
  const [filtroTipo, setFiltroTipo] = useState('todos');
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);

  const getColorClasses = (notif) => {
    const colorAlerta = notif.colorAlerta;
    const tipo = notif.tipo;

    if (tipo === 'ALERTA_PREVENTIVA') {
      if (colorAlerta === 'verde') {
        return { bg: 'bg-emerald-50', border: 'border-emerald-200', iconColor: 'text-emerald-600' };
      }
      if (colorAlerta === 'amarillo' || colorAlerta === 'naranja') {
        return { bg: 'bg-amber-50', border: 'border-amber-200', iconColor: 'text-amber-600' };
      }
    }

    if (tipo === 'ULTIMO_DIA_SIN_DEMORAS') {
      return { bg: 'bg-red-50', border: 'border-red-200', iconColor: 'text-red-600' };
    }

    if (tipo === 'DEMORAS_ACTIVAS') {
      return { bg: 'bg-red-100', border: 'border-red-400', iconColor: 'text-red-700' };
    }

    return { bg: 'bg-ink-50', border: 'border-ink-200', iconColor: 'text-ink-500' };
  };

  const getIcon = (notif) => {
    if (notif.tipo === 'ALERTA_PREVENTIVA') return <Calendar size={18} />;
    if (notif.tipo === 'ULTIMO_DIA_SIN_DEMORAS') return <AlertTriangle size={18} />;
    if (notif.tipo === 'DEMORAS_ACTIVAS') return <AlertCircle size={18} />;
    return <Bell size={18} />;
  };

  const getBadgeConfig = (notif) => {
    const { tipo, colorAlerta, diasRestantes, diasDemora } = notif;

    if (tipo === 'ALERTA_PREVENTIVA') {
      if (colorAlerta === 'verde') {
        return { text: `${diasRestantes}d`, color: 'bg-emerald-100 text-emerald-700' };
      }
      if (colorAlerta === 'amarillo' || colorAlerta === 'naranja') {
        return { text: `${diasRestantes}d`, color: 'bg-amber-100 text-amber-700' };
      }
    }
    if (tipo === 'ULTIMO_DIA_SIN_DEMORAS') {
      return { text: diasRestantes === 0 ? 'HOY' : `${diasRestantes}d`, color: 'bg-red-100 text-red-700' };
    }
    if (tipo === 'DEMORAS_ACTIVAS') {
      return { text: `${diasDemora}d demora`, color: 'bg-red-600 text-white' };
    }
    return { text: 'Info', color: 'bg-ink-100 text-ink-600' };
  };

  const handleRecalcular = async () => {
    setRecalculando(true);
    await forzarRecalculo();
    setRecalculando(false);
  };

  const formatFecha = (fecha) => {
    if (!fecha) return '';
    const date = new Date(fecha);
    const ahora = new Date();
    const diffMs = ahora - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHoras = Math.floor(diffMs / 3600000);
    const diffDias = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Ahora';
    if (diffMins < 60) return `${diffMins}m`;
    if (diffHoras < 24) return `${diffHoras}h`;
    if (diffDias < 7) return `${diffDias}d`;
    return date.toLocaleDateString('es-MX');
  };

  // Conteos por rango de días:
  // Verde (Preventivo): 5-4 días antes
  const conteoVerde = notificaciones.filter(n =>
    n.colorAlerta === 'verde' && n.diasRestantes >= 4 && n.diasRestantes <= 5
  ).length;

  // Amarillo (Alerta): 3-2 días antes
  const conteoAmarillo = notificaciones.filter(n =>
    (n.colorAlerta === 'amarillo' || n.colorAlerta === 'naranja') && n.diasRestantes >= 2 && n.diasRestantes <= 3
  ).length;

  // Rojo (Alerta Crítica): 1-0 días antes
  const conteoRojo = notificaciones.filter(n =>
    n.tipo === 'ULTIMO_DIA_SIN_DEMORAS'
  ).length;

  // Con Demoras: Ya generando demoras (días negativos)
  const conteoDemoras = notificaciones.filter(n =>
    n.tipo === 'DEMORAS_ACTIVAS'
  ).length;

  const notificacionesFiltradas = notificaciones.filter(n => {
    if (filtroTipo === 'verde') return n.colorAlerta === 'verde';
    if (filtroTipo === 'amarillo') return n.colorAlerta === 'amarillo' || n.colorAlerta === 'naranja';
    if (filtroTipo === 'rojo') return n.tipo === 'ULTIMO_DIA_SIN_DEMORAS';
    if (filtroTipo === 'demoras') return n.tipo === 'DEMORAS_ACTIVAS';
    return true;
  });

  return (
    <>
      {/* Bell Button */}
      <button
        onClick={() => setShowModal(true)}
        className="relative p-2 text-ink-400 hover:text-ink-600 hover:bg-ink-50 rounded-lg transition-colors"
      >
        <Bell size={20} />
        {resumen.total > 0 && (
          <span className={`
            absolute -top-0.5 -right-0.5
            min-w-[18px] h-[18px] px-1
            text-[10px] font-semibold text-white
            rounded-full flex items-center justify-center
            ${resumen.criticas > 0 ? 'bg-red-500' : 'bg-ink-500'}
          `}>
            {resumen.total > 99 ? '99+' : resumen.total}
          </span>
        )}
      </button>

      {/* Modal */}
      {showModal && (
        <div
          className="fixed inset-0 bg-ink-900/30 backdrop-blur-sm flex items-start justify-center pt-20 z-50 animate-fade-in"
          onClick={() => setShowModal(false)}
        >
          <div
            className="bg-white rounded-xl shadow-lifted w-full max-w-lg max-h-[75vh] overflow-hidden mx-4 animate-scale-in"
            onClick={e => e.stopPropagation()}
          >
            {/* Header */}
            <div className="px-5 py-4 border-b border-ink-100 flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-ink-900">Alertas</h2>
                <p className="text-xs text-ink-400 mt-0.5">
                  {ultimaActualizacion ? `Actualizado ${formatFecha(ultimaActualizacion)}` : 'Cargando...'}
                </p>
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={handleRecalcular}
                  disabled={recalculando}
                  className="p-2 text-ink-400 hover:text-ink-600 hover:bg-ink-50 rounded-lg transition-colors disabled:opacity-50"
                >
                  <RefreshCw size={16} className={recalculando ? 'animate-spin' : ''} />
                </button>
                <button
                  onClick={() => setShowModal(false)}
                  className="p-2 text-ink-400 hover:text-ink-600 hover:bg-ink-50 rounded-lg transition-colors"
                >
                  <X size={18} />
                </button>
              </div>
            </div>

            {/* Stats - Botones de filtro por color */}
            <div className="grid grid-cols-4 gap-2 p-4 bg-ink-50 border-b border-ink-100">
              <button
                onClick={() => setFiltroTipo(filtroTipo === 'verde' ? 'todos' : 'verde')}
                className={`text-center p-2 rounded-lg transition-all ${
                  filtroTipo === 'verde'
                    ? 'bg-emerald-100 ring-2 ring-emerald-500'
                    : 'bg-white hover:bg-emerald-50'
                }`}
              >
                <p className="text-lg font-semibold text-emerald-600">{conteoVerde}</p>
                <p className="text-[10px] text-emerald-700 uppercase tracking-wide font-medium">5-4 días</p>
                <p className="text-[9px] text-emerald-600">Preventivo</p>
              </button>
              <button
                onClick={() => setFiltroTipo(filtroTipo === 'amarillo' ? 'todos' : 'amarillo')}
                className={`text-center p-2 rounded-lg transition-all ${
                  filtroTipo === 'amarillo'
                    ? 'bg-amber-100 ring-2 ring-amber-500'
                    : 'bg-white hover:bg-amber-50'
                }`}
              >
                <p className="text-lg font-semibold text-amber-600">{conteoAmarillo}</p>
                <p className="text-[10px] text-amber-700 uppercase tracking-wide font-medium">3-2 días</p>
                <p className="text-[9px] text-amber-600">Alerta</p>
              </button>
              <button
                onClick={() => setFiltroTipo(filtroTipo === 'rojo' ? 'todos' : 'rojo')}
                className={`text-center p-2 rounded-lg transition-all ${
                  filtroTipo === 'rojo'
                    ? 'bg-red-100 ring-2 ring-red-500'
                    : 'bg-white hover:bg-red-50'
                }`}
              >
                <p className="text-lg font-semibold text-red-500">{conteoRojo}</p>
                <p className="text-[10px] text-red-700 uppercase tracking-wide font-medium">1-0 días</p>
                <p className="text-[9px] text-red-600">Crítico</p>
              </button>
              <button
                onClick={() => setFiltroTipo(filtroTipo === 'demoras' ? 'todos' : 'demoras')}
                className={`text-center p-2 rounded-lg transition-all ${
                  filtroTipo === 'demoras'
                    ? 'bg-red-200 ring-2 ring-red-600'
                    : 'bg-white hover:bg-red-50'
                }`}
              >
                <p className="text-lg font-semibold text-red-700">{conteoDemoras}</p>
                <p className="text-[10px] text-red-800 uppercase tracking-wide font-medium">Demoras</p>
                <p className="text-[9px] text-red-700">Activas</p>
              </button>
            </div>

            {/* Filtro "Todas" */}
            <div className="px-4 py-2 border-b border-ink-100">
              <button
                onClick={() => setFiltroTipo('todos')}
                className={`
                  px-4 py-1.5 rounded-lg text-xs font-medium transition-colors w-full
                  ${filtroTipo === 'todos'
                    ? 'bg-ink-900 text-white'
                    : 'bg-ink-50 text-ink-500 hover:bg-ink-100'
                  }
                `}
              >
                Mostrar Todas ({notificaciones.length})
              </button>
            </div>

            {/* List */}
            <div className="overflow-y-auto max-h-[40vh]">
              {loading && notificaciones.length === 0 ? (
                <div className="flex items-center justify-center py-12">
                  <div className="w-6 h-6 border-2 border-ink-200 border-t-ink-600 rounded-full animate-spin" />
                </div>
              ) : notificacionesFiltradas.length === 0 ? (
                <div className="text-center py-12 px-4">
                  <div className="w-12 h-12 bg-ink-50 rounded-xl flex items-center justify-center mx-auto mb-3">
                    <Bell size={20} className="text-ink-300" />
                  </div>
                  <p className="text-sm text-ink-600">Sin alertas pendientes</p>
                  <p className="text-xs text-ink-400 mt-1">Todo está al día</p>
                </div>
              ) : (
                <div className="divide-y divide-ink-50">
                  {notificacionesFiltradas.map((notif) => {
                    const colorClasses = getColorClasses(notif);
                    const badge = getBadgeConfig(notif);
                    return (
                      <div
                        key={notif.id}
                        className="p-4 hover:bg-ink-50 transition-colors group cursor-pointer"
                        onClick={() => {
                          if (onContenedorClick) {
                            onContenedorClick(notif.contenedor);
                            setShowModal(false);
                          }
                        }}
                      >
                        <div className="flex items-start gap-3">
                          <div className={`w-9 h-9 ${colorClasses.bg} border ${colorClasses.border} rounded-lg flex items-center justify-center flex-shrink-0`}>
                            <span className={colorClasses.iconColor}>{getIcon(notif)}</span>
                          </div>

                          <div className="flex-1 min-w-0">
                            <div className="flex items-center justify-between gap-2 mb-1">
                              <p className="text-sm font-medium text-ink-800 truncate">{notif.contenedor}</p>
                              <span className={`px-2 py-0.5 rounded text-[10px] font-semibold flex-shrink-0 ${badge.color}`}>
                                {badge.text}
                              </span>
                            </div>
                            <p className="text-xs text-ink-500 line-clamp-2 mb-2">{notif.mensaje}</p>
                            <div className="flex items-center gap-3 text-[10px] text-ink-400">
                              <span>{notif.cliente}</span>
                              <span>{notif.naviera}</span>
                              {notif.costoAcumulado > 0 && (
                                <span className="text-red-600 font-medium">
                                  ${parseFloat(notif.costoAcumulado).toLocaleString('es-MX')}
                                </span>
                              )}
                            </div>
                          </div>

                          <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            <button
                              onClick={(e) => { e.stopPropagation(); setConfirmDeleteId(notif.id); }}
                              className="p-1.5 text-emerald-500 hover:bg-emerald-50 rounded-lg transition-colors"
                              title="Eliminar notificación"
                            >
                              <Check size={14} />
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Footer */}
            {notificaciones.length > 0 && (
              <div className="px-4 py-3 border-t border-ink-100 flex items-center justify-between">
                <button
                  onClick={marcarTodasComoLeidas}
                  className="text-xs text-ink-500 hover:text-ink-700 transition-colors"
                >
                  Marcar todas como leídas
                </button>
                <button
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 bg-ink-900 text-white text-xs font-medium rounded-lg hover:bg-ink-800 transition-colors"
                >
                  Cerrar
                </button>
              </div>
            )}
          </div>
        </div>
      )}
      {/* Modal de confirmación de eliminación */}
      {confirmDeleteId && (
        <div className="fixed inset-0 z-[110] flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-xl shadow-2xl p-6 max-w-sm w-full mx-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-2 bg-red-100 rounded-full">
                <AlertTriangle size={20} className="text-red-600" />
              </div>
              <h3 className="text-base font-semibold text-ink-900">Eliminar notificación</h3>
            </div>
            <p className="text-sm text-ink-600 mb-6">
              ¿Estás seguro de que deseas eliminar esta notificación? Esta acción es <span className="font-semibold text-red-600">irreversible</span>.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setConfirmDeleteId(null)}
                className="px-4 py-2 text-sm font-medium text-ink-600 bg-ink-100 rounded-lg hover:bg-ink-200 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={() => {
                  descartarNotificacion(confirmDeleteId);
                  setConfirmDeleteId(null);
                }}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors"
              >
                Eliminar
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default NotificacionesCampana;
